// src/pages/BlogPost.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Edit2, Send, CornerDownRight, Trash2 } from "lucide-react";
import { blogsData } from "../data/blogsData";
import { blogs as staticBlogs } from "../data/mock";
import { supabase, SUPABASE_READY } from "../utils/supabase";
import { useAdminAuth } from "../hooks/useAdminAuth";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ── Fingerprint (reuse same logic) ───────────────────────────────────────────
const getFingerprint = () => {
  const signals = [navigator.userAgent, navigator.language, `${screen.width}x${screen.height}`, screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone, navigator.hardwareConcurrency || 0, navigator.platform || ""].join("|");
  let hash = 5381;
  for (let i = 0; i < signals.length; i++) hash = (hash * 33) ^ signals.charCodeAt(i);
  return (hash >>> 0).toString(36);
};

// ── Color maps ────────────────────────────────────────────────────────────────
const TEXT_COLOR   = { default: "text-foreground", muted: "text-muted-foreground", green: "text-emerald-500", red: "text-red-500", blue: "text-blue-500", yellow: "text-yellow-400", purple: "text-violet-500" };
const BORDER_COLOR = { default: "border-foreground", muted: "border-muted-foreground", green: "border-emerald-500", red: "border-red-500", blue: "border-blue-500", yellow: "border-yellow-400", purple: "border-violet-500" };
const BG_COLOR     = { default: "bg-foreground/5", muted: "bg-muted/50", green: "bg-emerald-500/10", red: "bg-red-500/10", blue: "bg-blue-500/10", yellow: "bg-yellow-400/10", purple: "bg-violet-500/10" };
const IMAGE_SIZE   = { sm: "max-w-sm", md: "max-w-xl", lg: "max-w-3xl", full: "max-w-full" };

// ── Content block renderer ────────────────────────────────────────────────────
const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return <p className={TEXT_COLOR[block.color] || TEXT_COLOR.default}>{block.text}</p>;
    case "h2":
      return <h2 className={`font-display text-2xl md:text-3xl tracking-tight !mt-14 !mb-2 ${TEXT_COLOR[block.color] || TEXT_COLOR.default}`}>{block.text}</h2>;
    case "h3":
      return <h3 className={`font-display text-xl md:text-2xl tracking-tight !mt-10 !mb-1 ${TEXT_COLOR[block.color] || TEXT_COLOR.default}`}>{block.text}</h3>;
    case "blockquote": {
      const a = block.accentColor || "default";
      return <blockquote className={`border-l-2 ${BORDER_COLOR[a]} pl-6 italic text-xl !my-10`}>&ldquo;{block.text}&rdquo;</blockquote>;
    }
    case "callout": {
      const c = block.color || "blue";
      return (
        <div className={`!my-8 flex gap-4 rounded-md border ${BORDER_COLOR[c]} ${BG_COLOR[c]} px-5 py-4`}>
          {block.icon && <span className={`font-mono text-base flex-shrink-0 mt-0.5 ${TEXT_COLOR[c]}`}>{block.icon}</span>}
          <p className={`text-sm leading-relaxed m-0 ${TEXT_COLOR[c]}`}>{block.text}</p>
        </div>
      );
    }
    case "list": {
      const mc = block.markerColor || "default";
      return (
        <ul className="space-y-3 list-none pl-0 !my-6">
          {(block.items || []).map((item, i) => {
            const isObj = typeof item === "object";
            return (
              <li key={i} className="flex gap-3">
                <span className={`font-mono text-xs mt-1 flex-shrink-0 ${TEXT_COLOR[mc]}`}>0{i + 1} —</span>
                <span className={TEXT_COLOR[isObj ? item.color || "default" : "default"]}>{isObj ? item.text : item}</span>
              </li>
            );
          })}
        </ul>
      );
    }
    case "code":
      return (
        <div className="!my-8">
          {block.lang && <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-5 py-2 border hairline border-b-0 rounded-t-md bg-muted/30">{block.lang}</div>}
          <pre className={`overflow-x-auto border hairline ${block.lang ? "rounded-b-md rounded-t-none" : "rounded-md"} bg-muted/50 px-5 py-5 text-[13px] leading-relaxed font-mono whitespace-pre`}>
            <code>{block.text}</code>
          </pre>
        </div>
      );
    case "image": {
      const sz = IMAGE_SIZE[block.size || "lg"];
      return (
        <figure className={`!my-10 ${sz}`}>
          <img src={block.src} alt={block.alt || ""} className="w-full rounded-md border hairline object-cover" loading="lazy" />
          {block.caption && <figcaption className="mt-3 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">{block.caption}</figcaption>}
        </figure>
      );
    }
    case "divider":
      return <hr className="!my-12 border-0 border-t hairline" />;
    default:
      return null;
  }
};

// ── Comment form ──────────────────────────────────────────────────────────────
const CommentForm = ({ postSlug, parentId = null, onSuccess, onCancel, compact = false }) => {
  const [name, setName]       = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]       = useState(false);
  const fp = useRef(getFingerprint());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || submitting) return;
    setSubmitting(true);

    const comment = {
      post_slug:  postSlug,
      parent_id:  parentId || null,
      name:       name.trim() || null,
      message:    message.trim(),
      fp:         fp.current,
      created_at: new Date().toISOString(),
    };

    if (SUPABASE_READY) {
      const { error } = await supabase.from("blog_comments").insert(comment);
      if (error) { setSubmitting(false); return; }
    }

    setDone(true);
    setName(""); setMessage("");
    setSubmitting(false);
    onSuccess?.({ ...comment, id: Date.now().toString() });
  };

  if (done && compact) return (
    <p className="font-mono text-[11px] text-emerald-500 uppercase tracking-widest py-2">Comment posted ✓</p>
  );

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${compact ? "" : "mt-8"}`}>
      {!compact && <p className="font-display text-xl">Leave a comment.</p>}
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        className="w-full bg-transparent border hairline rounded-md px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
      />
      <div className="relative">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={compact ? "Write a reply…" : "Share your thoughts, questions or feedback…"}
          rows={compact ? 2 : 4}
          required
          className="w-full bg-transparent border hairline rounded-md px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 resize-none"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting || !message.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-foreground text-background text-xs font-mono uppercase tracking-widest rounded-md hover:opacity-80 transition-opacity disabled:opacity-40"
        >
          <Send size={11} /> {submitting ? "Posting…" : compact ? "Reply" : "Post comment"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 border hairline rounded-md text-xs font-mono uppercase tracking-widest text-muted-foreground hover:bg-muted transition-colors">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

// ── Comment card ──────────────────────────────────────────────────────────────
const CommentCard = ({ comment, postSlug, onReply, allComments, isAdmin, onDelete, depth = 0 }) => {
  const [showReply, setShowReply] = useState(false);
  const initials = comment.name
    ? comment.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "AN";

  const replies = allComments.filter((c) => c.parent_id === comment.id);

  return (
    <div className={`${depth > 0 ? "ml-8 mt-4" : "border-t hairline pt-6 mt-6"}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full border hairline grid place-items-center bg-muted/30 flex-shrink-0 mt-0.5">
          <span className="font-mono text-[10px] font-bold">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1.5 flex-wrap">
            <span className="text-sm font-medium">{comment.name || "Anonymous"}</span>
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              {timeAgo(comment.created_at)}
            </span>
            {depth > 0 && <CornerDownRight size={12} className="text-muted-foreground" />}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{comment.message}</p>
          <div className="flex items-center gap-4 mt-2">
            <button
              onClick={() => setShowReply((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            >
              {showReply ? "Cancel" : "↩ Reply"}
            </button>
            {isAdmin && (
              <button
                onClick={() => onDelete(comment.id)}
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-red-500 transition-colors"
              >
                <Trash2 size={10} className="inline mr-1" />Delete
              </button>
            )}
          </div>
          {showReply && (
            <div className="mt-3">
              <CommentForm
                postSlug={postSlug}
                parentId={comment.id}
                compact
                onSuccess={(c) => { onReply(c); setShowReply(false); }}
                onCancel={() => setShowReply(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {replies.map((r) => (
        <CommentCard
          key={r.id}
          comment={r}
          postSlug={postSlug}
          onReply={onReply}
          allComments={allComments}
          isAdmin={isAdmin}
          onDelete={onDelete}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

// ── BlogPost page ─────────────────────────────────────────────────────────────
const BlogPost = () => {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const { isAdmin }     = useAdminAuth();
  const [dbPost, setDbPost]       = useState(null);
  const [comments, setComments]   = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);

  // Find post: check DB first, fall back to static data
  const staticPost = [...blogsData, ...staticBlogs].find((b) => b.id === id || b.slug === id);
  const post = dbPost || staticPost;

  const others = [...blogsData, ...staticBlogs]
    .filter((b) => (b.id || b.slug) !== id)
    .slice(0, 3);

  // Load from DB
  useEffect(() => {
    if (!SUPABASE_READY) return;
    supabase.from("blog_posts").select("*").eq("slug", id).single()
      .then(({ data }) => {
        if (data) setDbPost({
          ...data,
          id: data.slug,
          readTime: data.read_time,
          heroImage: data.hero_image,
        });
      });
  }, [id]);

  // Load comments
  useEffect(() => {
    const load = async () => {
      if (!SUPABASE_READY) { setLoadingComments(false); return; }
      const { data } = await supabase
        .from("blog_comments")
        .select("*")
        .eq("post_slug", id)
        .order("created_at", { ascending: true });
      setComments(data || []);
      setLoadingComments(false);
    };
    load();
  }, [id]);

  const handleNewComment = (c) => setComments((prev) => [...prev, c]);

  const handleDeleteComment = async (commentId) => {
    if (!isAdmin || !SUPABASE_READY) return;
    await supabase.from("blog_comments").delete().eq("id", commentId);
    setComments((prev) => prev.filter((c) => c.id !== commentId));
  };

  // Top-level comments only
  const topLevel = comments.filter((c) => !c.parent_id);

  if (!post) return (
    <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
      <p className="text-muted-foreground">Post not found.</p>
      <Link to="/journal" className="mt-4 inline-flex items-center gap-2 text-sm link-grow"><ArrowLeft size={14} /> Back to journal</Link>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <section className="border-b hairline">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
          <div className="flex items-center justify-between">
            <Link to="/journal" className="inline-flex items-center gap-2 font-mono text-[11px] text-muted-foreground uppercase tracking-widest link-grow" data-cursor-hover>
              <ArrowLeft size={14} /> Back to journal
            </Link>
            {isAdmin && (
              <Link
                to={`/journal/${id}/edit`}
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              >
                <Edit2 size={12} /> Edit
              </Link>
            )}
          </div>

          <div className="mt-8 font-mono text-[11px] text-muted-foreground uppercase tracking-widest flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {formatDate(post.date || post.created_at)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {post.readTime || post.read_time}</span>
            <span>— {post.category}</span>
          </div>

          <h1 className="display-xl text-4xl md:text-6xl leading-[1.02] mt-6">{post.title}</h1>
          <p className="mt-6 text-muted-foreground text-lg leading-relaxed">{post.excerpt}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {(post.tags || []).map((t) => (
              <span key={t} className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border hairline rounded-full">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Hero image */}
      {(post.heroImage?.src || post.hero_image?.src) && (
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-10">
          <img
            src={post.heroImage?.src || post.hero_image?.src}
            alt={post.heroImage?.alt || ""}
            className="w-full rounded-md border hairline object-cover max-h-[420px]"
          />
        </div>
      )}

      {/* Article body */}
      <article className="max-w-[780px] mx-auto px-6 md:px-10 py-16 md:py-24 space-y-6 text-[17px] leading-[1.75] text-foreground/90">
        {post.content && post.content.length > 0 ? (
          post.content.map((block, i) => <Block key={i} block={block} />)
        ) : (
          <p className="text-muted-foreground">Full write-up coming soon.</p>
        )}
      </article>

      {/* ── Comments ── */}
      <section className="border-t hairline">
        <div className="max-w-[780px] mx-auto px-6 md:px-10 py-16">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-display text-3xl md:text-4xl">
              {loadingComments ? "Comments" : `${comments.length} comment${comments.length !== 1 ? "s" : ""}.`}
            </h2>
          </div>

          {/* Comment form */}
          <CommentForm postSlug={id} onSuccess={handleNewComment} />

          {/* Comments list */}
          {!loadingComments && topLevel.length === 0 && (
            <div className="mt-12 text-center">
              <p className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                No comments yet. Be the first.
              </p>
            </div>
          )}

          {topLevel.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              postSlug={id}
              onReply={handleNewComment}
              allComments={comments}
              isAdmin={isAdmin}
              onDelete={handleDeleteComment}
            />
          ))}
        </div>
      </section>

      {/* More posts */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="flex items-end justify-between gap-6 mb-10">
            <h3 className="display-xl text-3xl md:text-5xl">More reading.</h3>
            <Link to="/journal" className="inline-flex items-center gap-2 link-grow text-sm" data-cursor-hover>
              All posts <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((b) => (
              <Link key={b.id} to={`/journal/${b.id}`} className="group p-6 border hairline rounded-md hover:bg-muted/40 transition-colors" data-cursor-hover>
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                  {formatDate(b.date)} · {b.readTime}
                </div>
                <h4 className="font-display text-xl md:text-2xl mt-3 leading-tight">{b.title}</h4>
                <div className="mt-6 inline-flex items-center gap-2 text-sm link-grow">Read <ArrowUpRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;